import styled, { css } from 'styled-components'
import React, { FunctionComponent, useCallback, useMemo } from 'react'
import debounce from 'lodash/debounce'
import Grid from '../Grid'
import {
    ClickableProps,
    CommonProps,
    FlexboxProps,
    GridProps,
    WebTaboolaRegionItemProps,
} from '../../styles/types'
import { clickable, flexbox } from '../../styles'
import usePointerEvents from '../../hooks/usePointerEvents/index.web'
import { space, SpacingProps } from '../../styles/properties/space'
import { color, ColorProps } from '../../styles/properties/color'

export type TouchableProps = CommonProps &
    Pick<FlexboxProps, 'flexBasis'> &
    ClickableProps &
    HoverAndActiveProps &
    SpacingProps &
    Pick<ColorProps, 'backgroundColor'> &
    WebTaboolaRegionItemProps & {
        onPress?: () => void
        onLongPress?: () => void
        skipDebounce?: boolean
        href?: string
        webTarget?: '_blank' | '_self' | '_parent' | '_top' | 'framename' | string
        expand?: boolean
        webRel?: (href?: string) => string | undefined
    }

type HoverAndActiveProps = {
    activeOpacity?: number
    disableHover?: boolean
}

const DEFAULT_HOVER_OPACITY = 0.7
const hoverAndActive = css<HoverAndActiveProps>`
    @media (hover: hover) {
        :hover {
            opacity: ${(props): string =>
                `${
                    !props.disableHover
                        ? `${Math.max(DEFAULT_HOVER_OPACITY, props.activeOpacity || 0)}`
                        : ''
                }`};
            transition: 0s;
        }
    }
    :active {
        opacity: ${(props): string => `${props.activeOpacity}`};
        transition: 0s;
    }
`

export const TouchableGrid = styled(Grid)<GridProps & HoverAndActiveProps>`
    ${hoverAndActive}
    ${clickable}
    ${space}
`
TouchableGrid.displayName = 'TouchableGrid'

export const Clickable = styled.a<GridProps & HoverAndActiveProps>`
    text-decoration: none;
    flex-direction: row;
    transition: 0s 0.1s;
    ${props => !props.noExpand && flexbox}
    ${hoverAndActive}
    ${clickable}
    ${space}
    ${color}
`

const DEFAULT_ACTIVE_OPACITY = 0.2

const Touchable: FunctionComponent<TouchableProps> = ({
    children,
    href,
    webTarget,
    skipDebounce,
    onPress,
    onLongPress,
    testID,
    activeOpacity = DEFAULT_ACTIVE_OPACITY,
    disableHover,
    expand,
    flexBasis,
    pointer,
    pointerEvents,
    webTBRegionItem,
    webRel,
    ...rest
}) => {
    const debouncedPress = useMemo(
        () =>
            onPress
                ? debounce(onPress, 200, {
                      leading: true,
                      trailing: false,
                  })
                : undefined,
        [onPress]
    )

    const onPressCallback = useCallback(
        event => {
            if (onPress) {
                event.stopPropagation()
                skipDebounce ? onPress() : debouncedPress && debouncedPress()
            }
        },
        [onPress, skipDebounce, debouncedPress]
    )

    const clickEvents = usePointerEvents({
        onLongPress,
        onPress: onPressCallback,
    })

    return href ? (
        <Clickable
            data-testid={testID}
            data-tb-region-item={webTBRegionItem}
            href={href}
            target={webTarget}
            noExpand={!expand}
            width={expand ? '100%' : undefined}
            flexShrink={0}
            flexBasis={flexBasis}
            pointer={pointer || true}
            pointerEvents={pointerEvents}
            activeOpacity={activeOpacity}
            disableHover={disableHover}
            rel={webRel?.(href)}
            {...clickEvents}
            {...rest}
        >
            {children}
        </Clickable>
    ) : (
        <TouchableGrid
            data-testid={testID}
            data-tb-region-item={webTBRegionItem}
            noExpand={!expand}
            width={expand ? '100%' : undefined}
            flexShrink={0}
            flexBasis={flexBasis}
            pointer={pointer !== undefined ? pointer : !!onPress}
            pointerEvents={pointerEvents}
            activeOpacity={onPress || onLongPress ? activeOpacity : 1}
            disableHover={disableHover}
            {...clickEvents}
            {...rest}
        >
            {children}
        </TouchableGrid>
    )
}

export default Touchable
